import React, { useMemo, useEffect, useState } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js/lib/core';

import bar from 'plotly.js/lib/bar';
import scatter from 'plotly.js/lib/scatter';

import useAdaptiveData from '../../hooks/useAdaptiveData';

Plotly.register([bar, scatter]);
const Plot = createPlotlyComponent(Plotly);

const AdaptiveProjectChart = ({ data }) => {
  const currentData = useMemo(
    () => ({
      netSuiteId: data?.netSuiteId || '',
      afeNumber: data?.afeNumber || '',
    }),
    [data?.netSuiteId, data?.afeNumber]
  );

  const { adaptiveForecastData, adaptiveData, adaptiveActuals, loading, error } = useAdaptiveData(currentData);

  if (loading) return <p>Loading chart data...</p>;
  if (error) return <p>Error fetching data: {error}</p>;
  if (!adaptiveForecastData || adaptiveForecastData.length === 0) return <p>No forecast data available.</p>;

  const months = adaptiveForecastData.map((d) => d.monthYear);
  const monthlyForecast = adaptiveForecastData.map((d) => Number(d.monthlyForecast) || 0);
  const cumulativeSpend = adaptiveForecastData.map((d) => Number(d.cumulativeSpend) || 0);
  const budgetCost = adaptiveData.map((d) => Number(d.budgetCost) || 0);
  const baseCost = adaptiveData.map((d) => Number(d.baseCost) || 0);
  const poActuals = adaptiveActuals?.map((d) => Number(d.poActuals) || 0) || [];

  const formatValue = (y) => {
    if (y >= 1_000_000) {
      return (y / 1_000_000).toFixed(4) + 'M';
    } else {
      return (y / 1_000).toFixed(4) + 'K';
    }
  };

  const dataSeries = [
    {
      x: months,
      y: monthlyForecast,
      type: 'bar',
      name: 'Monthly Forecast',
      marker: { color: '#FFA500', opacity: 0.8 },
      yaxis: 'y',
      hovertemplate: '%{customdata}<extra></extra>',
      customdata: monthlyForecast.map((y) => formatValue(y)),
    },
    {
      x: months,
      y: poActuals,
      type: 'bar',
      name: 'Monthly Actuals',
      marker: { color: '#4682B4', opacity: 0.8 },
      yaxis: 'y',
      hovertemplate: '%{customdata}<extra></extra>',
      customdata: poActuals.map((y) => formatValue(y)),
    },
    {
      x: months,
      y: baseCost,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Base Cost',
      line: { color: '#00897B', width: 2 },
      marker: { color: '#00897B', size: 2 },
      yaxis: 'y2',
      hovertemplate: '%{customdata}<extra></extra>',
      customdata: baseCost.map((y) => formatValue(y)),
    },
    {
      x: months,
      y: cumulativeSpend,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Cumulative Spend',
      line: { color: '#B0C3E5', width: 2 },
      marker: { color: '#B0C3E5', size: 2 },
      yaxis: 'y2',
      hovertemplate: '%{customdata}<extra></extra>',
      customdata: cumulativeSpend.map((y) => formatValue(y)),
    },
    {
      x: months,
      y: budgetCost,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Budget',
      line: { color: '#D32F2F', width: 2 },
      marker: { color: '#D32F2F', size: 2 },
      yaxis: 'y2',
      hovertemplate: '%{customdata}<extra></extra>',
      customdata: budgetCost.map((y) => formatValue(y)),
    },
  ];

  const layout = {
    hovermode: 'x unified',
    autosize: true,
    responsive: true,

    xaxis: {
      title: 'Month/Year',
      tickangle: 90,
      automargin: true,
      autorange: true,
    },

    yaxis: {
      title: { text: 'Monthly Actuals | Monthly Forecast', standoff: 15 },
      showgrid: true,
      autorange: true,
    },

    yaxis2: {
      title: { text: 'Cumulative Spend | Base | Budget', standoff: 15 },
      overlaying: 'y',
      side: 'right',
      showgrid: false,
      autorange: true,
    },

    margin: { l: 100, r: 100, t: 80, b: 100 },
    legend: { x: 0.6, y: 0.1, font: { size: 12 }, bgcolor: 'rgba(255,255,255,0.7)' },
    barmode: 'group',
  };

  const config = {
    responsive: true,
    scrollZoom: false,
    displaylogo: false,
    modeBarButtonsToRemove: ['lasso2d', 'select2d', 'zoomIn2d', 'zoomOut2d'],
    displayModeBar: 'hover',
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '50vh',
        minHeight: '400px',
      }}
    >
      <Plot data={dataSeries} layout={layout} config={config} style={{ width: '90%', height: '90%' }} />
    </div>
  );
};

export default AdaptiveProjectChart;
